/**
 * Wrapper around fetch that automatically injects the JWT Authorization header
 * and handles silent token refresh on 401 responses.
 * Use this instead of raw fetch() for all /api/* requests.
 */

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api';

// Shared promise so multiple concurrent 401s only trigger one refresh call
let _refreshPromise: Promise<string | null> | null = null;

async function _doRefresh(): Promise<string | null> {
  const token = localStorage.getItem('authToken');
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.token) {
      localStorage.setItem('authToken', data.token);
      return data.token as string;
    }
    return null;
  } catch {
    return null;
  }
}

function getRefreshPromise(): Promise<string | null> {
  if (!_refreshPromise) {
    _refreshPromise = _doRefresh().finally(() => { _refreshPromise = null; });
  }
  return _refreshPromise;
}

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const token = localStorage.getItem('authToken');
  const headers = new Headers(init?.headers);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(input, { ...init, headers });

  if (response.status === 401 && token) {
    // Token may have expired — attempt silent refresh
    const newToken = await getRefreshPromise();
    if (newToken) {
      // Retry original request with fresh token
      const retryHeaders = new Headers(init?.headers);
      retryHeaders.set('Authorization', `Bearer ${newToken}`);
      return fetch(input, { ...init, headers: retryHeaders });
    }
    // Refresh also failed (account deactivated / secret changed) — force re-login
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('permissions');
    localStorage.removeItem('enabledFeatures');
    window.location.href = '/login';
  }

  return response;
}

